/**
 * AdminDashboard.tsx — Super Admin overview page.
 *
 * Shows stats cards (users, accounts, sessions, security alert),
 * system health summary, and recent activity feed.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DevicesIcon from '@mui/icons-material/Devices';
import GroupIcon from '@mui/icons-material/Group';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import TimerIcon from '@mui/icons-material/Timer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import axios from 'axios';
import API_BASE from '../../config/api';
import { useShieldTheme } from '../../theme/ThemeContext';
import AppearancePanel from '../../components/AppearancePanel';
import ComplianceUpdatesPanel from '../../components/ComplianceUpdatesPanel';
import ThreatIntelPanel from '../../components/ThreatIntelPanel';

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  color = 'primary.main',
  alert = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  alert?: boolean;
}) {
  return (
    <Card
      sx={{
        borderRadius: 2,
        border: alert ? '1px solid rgba(239,35,60,0.4)' : '1px solid rgba(255,255,255,0.06)',
        bgcolor: alert ? 'rgba(239,35,60,0.06)' : 'background.paper',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{ color }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
        <Typography variant="h4" fontWeight={700} sx={{ color: alert ? 'error.main' : 'text.primary' }}>
          {value}
        </Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Health dot ────────────────────────────────────────────────────────────────

function HealthRow({ name, status }: { name: string; status: string }) {
  const color = status === 'healthy' ? '#2ecc71' : status === 'degraded' ? '#f77f00' : '#ef233c';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
      <Typography variant="body2">{name}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
        <Typography variant="caption" sx={{ color, textTransform: 'capitalize' }}>{status}</Typography>
      </Box>
    </Box>
  );
}

// ── Activity feed ─────────────────────────────────────────────────────────────

interface ActivityEntry {
  id: string;
  user_id: string | null;
  action: string;
  ip_address: string;
  success: boolean;
  created_at: string;
  details?: Record<string, unknown>;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { tokens: t } = useShieldTheme();
  const [stats, setStats]       = useState<any>(null);
  const [health, setHealth]     = useState<any>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/admin/stats`),
      axios.get(`${API_BASE}/admin/health`),
      axios.get(`${API_BASE}/admin/logs?limit=10`),
    ])
      .then(([s, h, a]) => {
        setStats(s.data);
        setHealth(h.data.components ?? h.data);
        setActivity(Array.isArray(a.data) ? a.data : (a.data.logs ?? []));
      })
      .catch(() => setError('Failed to load admin data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box p={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  const failedLogins = stats?.security?.failed_logins_24h ?? 0;
  const securityAlert = failedLogins >= 10;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SecurityIcon color="primary" /> Admin Control Center
      </Typography>

      {securityAlert && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
          {failedLogins} failed login attempts in the last 24 hours. Review the Security Events page.
        </Alert>
      )}

      {/* Stats row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<GroupIcon />}
            label="Total Users"
            value={stats?.users?.total ?? 0}
            sub={`${stats?.users?.active ?? 0} active, ${stats?.users?.blocked ?? 0} blocked`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<BusinessIcon />}
            label="Partner Accounts"
            value={stats?.accounts?.total ?? 0}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<DevicesIcon />}
            label="Active Sessions"
            value={stats?.sessions?.active_right_now ?? 0}
            color="#2ecc71"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<WarningAmberIcon />}
            label="Failed Logins (24h)"
            value={failedLogins}
            color="error.main"
            alert={securityAlert}
          />
        </Grid>

        {/* Billing row */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<CreditCardIcon />}
            label="Monthly Recurring Revenue"
            value={`$${((stats?.billing?.mrr_cents ?? 0) / 100).toLocaleString()}`}
            sub="active + past due"
            color="#2ecc71"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<TimerIcon />}
            label="Partners in Trial"
            value={stats?.billing?.trial_count ?? 0}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<WarningAmberIcon />}
            label="Past Due"
            value={stats?.billing?.past_due_count ?? 0}
            color="error.main"
            alert={(stats?.billing?.past_due_count ?? 0) > 0}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* User breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon fontSize="small" /> Users by Role
            </Typography>
            {['customer', 'engineer', 'super_admin'].map((role) => (
              <Box key={role} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {role.replace('_', ' ')}
                </Typography>
                <Chip
                  label={stats?.users?.by_role?.[role] ?? 0}
                  size="small"
                  color={role === 'super_admin' ? 'error' : role === 'engineer' ? 'warning' : 'default'}
                />
              </Box>
            ))}
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Pending verification: {stats?.users?.pending_verification ?? 0}
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => navigate('/admin/users')}
              startIcon={<AccountCircleIcon />}
            >
              Manage Users
            </Button>
          </Paper>
        </Grid>

        {/* System health */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HealthAndSafetyIcon fontSize="small" /> System Health
            </Typography>
            {health ? (
              Object.entries(health).map(([key, val]: [string, any]) => (
                <HealthRow key={key} name={key.replace(/_/g, ' ')} status={val?.status ?? val} />
              ))
            ) : (
              <Typography color="text.secondary" variant="body2">No health data.</Typography>
            )}
            <Button
              fullWidth
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => navigate('/admin/health')}
            >
              Full Health Report
            </Button>
          </Paper>
        </Grid>

        {/* Recent activity */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Recent Activity</Typography>
            {activity.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No recent activity.</Typography>
            ) : (
              <Stack spacing={1}>
                {activity.slice(0, 8).map((a) => (
                  <Box key={a.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {a.action}
                      </Typography>
                      <Box
                        sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          bgcolor: a.success ? '#2ecc71' : 'error.main', flexShrink: 0,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.disabled">
                      {a.ip_address} · {new Date(a.created_at).toLocaleTimeString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
            <Button
              fullWidth
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => navigate('/admin/security')}
            >
              View Security Events
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Platform Appearance ───────────────────────────────────────── */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          mb:             1.5,
        }}>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ fontFamily: t.fontDisplay, color: 'text.primary' }}
            >
              Platform Appearance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Set the default visual theme and layout for the platform
            </Typography>
          </Box>
          <Box sx={{
            fontSize:     '11px',
            px:           1.5,
            py:           0.5,
            borderRadius: t.radiusFull,
            background:   t.primaryMuted,
            color:        t.primary,
            fontWeight:   700,
            border:       `1px solid ${t.primary}33`,
            whiteSpace:   'nowrap',
          }}>
            Admin Only
          </Box>
        </Box>
        <AppearancePanel showTitle={false} compact={false} adminMode={true} />
      </Box>

      {/* ── Compliance Auto-Updater ───────────────────────────────────── */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          mb:             1.5,
        }}>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ fontFamily: t.fontDisplay, color: 'text.primary' }}
            >
              Compliance Updates
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Prowler version tracking and regulation feed monitoring
            </Typography>
          </Box>
          <Box sx={{
            fontSize:     '11px',
            px:           1.5,
            py:           0.5,
            borderRadius: t.radiusFull,
            background:   t.primaryMuted,
            color:        t.primary,
            fontWeight:   700,
            border:       `1px solid ${t.primary}33`,
            whiteSpace:   'nowrap',
          }}>
            Admin Only
          </Box>
        </Box>
        <ComplianceUpdatesPanel compact={true} />
      </Box>

      {/* ── Threat Intelligence ───────────────────────────────────────── */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          mb:             1.5,
        }}>
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ fontFamily: t.fontDisplay, color: 'text.primary' }}
            >
              Threat Intelligence
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              CVE/NVD · CISA KEV · MITRE ATT&amp;CK · AI Tools · Provider IPs
            </Typography>
          </Box>
          <Box sx={{
            fontSize:     '11px',
            px:           1.5,
            py:           0.5,
            borderRadius: t.radiusFull,
            background:   t.primaryMuted,
            color:        t.primary,
            fontWeight:   700,
            border:       `1px solid ${t.primary}33`,
            whiteSpace:   'nowrap',
          }}>
            Admin Only
          </Box>
        </Box>
        <ThreatIntelPanel />
      </Box>
    </Box>
  );
}
